package at.theatervogelweide.admin.web.components

import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.key
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import at.theatervogelweide.admin.contract.AdminResult
import at.theatervogelweide.admin.contract.IMAGE_EXTENSIONS
import at.theatervogelweide.admin.contract.ImageDetails
import at.theatervogelweide.admin.contract.ImageCategory
import at.theatervogelweide.admin.contract.formatMessage
import at.theatervogelweide.admin.contract.MAX_IMAGE_BYTES
import at.theatervogelweide.admin.contract.MAX_IMAGE_MEGABYTES
import at.theatervogelweide.admin.contract.UploadedImage
import at.theatervogelweide.admin.contract.moved
import kotlinx.coroutines.CancellationException
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.launch
import org.jetbrains.compose.web.attributes.ButtonType
import org.jetbrains.compose.web.attributes.InputType
import org.jetbrains.compose.web.attributes.alt
import org.jetbrains.compose.web.attributes.type
import org.jetbrains.compose.web.dom.Button
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.Img
import org.jetbrains.compose.web.dom.Input
import org.jetbrains.compose.web.dom.Label
import org.jetbrains.compose.web.dom.Span
import org.jetbrains.compose.web.dom.Text
import org.w3c.dom.asList
import org.w3c.dom.url.URL
import org.w3c.files.File

private val uploader = Bem("admin-image-uploader")
private val field = Bem("admin-field")
private val reorderBlock = Bem("admin-reorder")

/**
 * One image of an [EditableImages] list. [uuid] is `null` while the upload runs; [previewUrl] is the local preview
 * shown until the server answers.
 */
class EditableImage internal constructor(
    val key: String,
    val uuid: String?,
    internal val previewUrl: String?,
)

/**
 * The images of a record while it is edited: uploads start at once and show a local preview, removing and reordering
 * only change this list. The record's save then sends [uuids] and [currentDetails]. Pass [upload], usually
 * `AdminApi.uploadImage`, the saved [details] of the images (#105), and for a category with other formats than
 * [IMAGE_EXTENSIONS] its [extensions] and [formatMessage] (sponsor logos, #94).
 */
class EditableImages(
    private val scope: CoroutineScope,
    private val upload: suspend (File) -> AdminResult<UploadedImage>,
    uuids: List<String> = emptyList(),
    val extensions: Set<String> = IMAGE_EXTENSIONS,
    private val formatMessage: (String) -> String = ImageCategory.ACTS::formatMessage,
    details: Map<String, ImageDetails> = emptyMap(),
) {
    var images: List<EditableImage> by mutableStateOf(uuids.map { EditableImage(it, it, null) })
        private set

    /** The saved details of the images, as changed with "Übernehmen"; each keeps its version (ImageDetails.version). */
    private var details: Map<String, ImageDetails> by mutableStateOf(details)

    fun detailsOf(uuid: String): ImageDetails = details[uuid] ?: ImageDetails.NONE

    fun setDetails(uuid: String, value: ImageDetails) {
        details = details + (uuid to value)
    }

    /**
     * The details of the images the record keeps, as loaded or changed, to compare with the loaded ones: empty details
     * of an image without saved ones are no change.
     */
    val currentDetails: Map<String, ImageDetails>
        get() = uuids.toSet().let { kept -> details.filter { (uuid, value) -> uuid in kept && (value.hasContent || value.version != null) } }

    /** What the save sends: an entry for every image it keeps, so details cleared here are cleared on the server too. */
    val detailsToSave: Map<String, ImageDetails> get() = uuids.associateWith(::detailsOf)

    /** Why the last upload was refused, shown below the tiles. */
    var message: String? by mutableStateOf(null)
        private set

    private var nextUpload = 0

    /** The uploaded images in order; images still uploading are left out. */
    val uuids: List<String> get() = images.mapNotNull { it.uuid }

    val uploading: Boolean get() = images.any { it.uuid == null }

    fun add(files: List<File>) {
        message = null
        files.forEach { file ->
            val extension = file.name.substringAfterLast('.', "").lowercase()
            when {
                extension !in extensions -> message = formatMessage(file.name)
                file.size.toLong() > MAX_IMAGE_BYTES -> message = "„${file.name}“ ist größer als $MAX_IMAGE_MEGABYTES MB."
                else -> start(file)
            }
        }
    }

    private fun start(file: File) {
        val tile = EditableImage("upload-${nextUpload++}", null, URL.createObjectURL(file))
        images = images + tile

        scope.launch {
            val result = try {
                upload(file)
            } catch (exception: CancellationException) {
                // the editor closed; its previews are freed with it
                throw exception
            } catch (exception: Throwable) {
                // a failed fetch rejects with a JavaScript TypeError, which isn't a Kotlin Exception
                console.error("Uploading ${file.name} failed", exception)
                null
            }
            if (images.none { it.key == tile.key }) {
                // removed while uploading
                URL.revokeObjectURL(tile.previewUrl!!)
                return@launch
            }
            when (result) {
                is AdminResult.Success -> {
                    // from now on the tile shows the server's copy
                    URL.revokeObjectURL(tile.previewUrl!!)
                    replace(tile.key) { EditableImage(it.key, result.value.uuid, null) }
                }
                is AdminResult.Failure -> {
                    message = result.error.fieldErrors.firstOrNull()?.message ?: result.error.message
                    remove(tile.key)
                }

                null -> {
                    message = "„${file.name}“ konnte nicht hochgeladen werden. Bitte versuche es noch einmal."
                    remove(tile.key)
                }
            }
        }
    }

    fun remove(key: String) {
        images.firstOrNull { it.key == key }?.previewUrl?.let { URL.revokeObjectURL(it) }
        images = images.filterNot { it.key == key }
    }

    fun move(from: Int, to: Int) {
        images = images.moved(from, to)
    }

    private fun replace(key: String, change: (EditableImage) -> EditableImage) {
        images = images.map { if (it.key == key) change(it) else it }
    }

    /** Frees the local previews; call when the editor closes. */
    fun dispose() {
        images.mapNotNull { it.previewUrl }.forEach { URL.revokeObjectURL(it) }
    }
}

/**
 * The images of a record as tiles: the first is the main image, a drop zone adds more, each tile can be removed and
 * moved. [imageUrl] answers with the server's address of an uploaded image; [compact] is the small news variant.
 * Without [firstIsMain] no image is marked, for records whose main image is a field of its own
 * (see [AdminSingleImageField]).
 */
@Composable
fun AdminImageUploader(
    id: String,
    label: String,
    images: EditableImages,
    imageUrl: (String) -> String,
    compact: Boolean = false,
    firstIsMain: Boolean = true,
    error: String? = null,
    /** Ignores dropped files, e.g. while the record is saved; a surrounding disabled `<fieldset>` covers the buttons. */
    disabled: Boolean = false,
) {
    var dragOver by remember { mutableStateOf(false) }
    // the image whose details are open
    var editing by remember { mutableStateOf<String?>(null) }
    val reorder = rememberReorder(
        keys = images.images.map { it.key },
        describe = { key -> "Bild ${images.images.indexOfFirst { it.key == key } + 1}" },
        axis = ReorderAxis.BOTH,
        onMove = images::move,
    )
    val messageId = "$id-message"

    Div({
        classes(field.block)
        attr("role", "group")
        attr("aria-labelledby", "$id-label")
    }) {
        Span({
            classes(field.element("label").className)
            id("$id-label")
        }) { Text(label) }

        Div({
            classes(*listOfNotNull(uploader.block, uploader.modifier("compact").takeIf { compact }, reorderBlock.block).toTypedArray())
        }) {
            images.images.forEachIndexed { index, image ->
                key(image.key) {
                    val number = index + 1
                    Div({
                        classes(
                            *listOfNotNull(
                                uploader.element("image").className,
                                uploader.element("image").modifier("main").takeIf { index == 0 && !compact && firstIsMain },
                            ).toTypedArray(),
                        )
                        reorderItem(reorder, image.key)
                        if (image.uuid == null) {
                            attr("aria-busy", "true")
                        }
                    }) {
                        Img(image.previewUrl ?: imageUrl(image.uuid!!), attrs = {
                            classes(uploader.element("img").className)
                            alt(if (index == 0 && firstIsMain) "Hauptbild" else "Bild $number")
                        })
                        image.uuid?.let { uuid ->
                            ImageDetailsButton("Bilddetails zu Bild $number") { editing = uuid }
                        }
                        ReorderHandle(reorder, image.key, "Bild $number verschieben", onImage = true)
                        ImageBadges(main = index == 0 && firstIsMain, missingDescription = image.uuid?.let { !images.detailsOf(it).hasDescription } == true)
                        if (image.uuid == null) {
                            Span({ classes("admin-visually-hidden") }) { Text("Wird hochgeladen …") }
                        }
                        Button({
                            type(ButtonType.Button)
                            classes(uploader.element("remove").className)
                            attr("aria-label", "Bild $number entfernen")
                            onClick { images.remove(image.key) }
                        }) { Span({ attr("aria-hidden", "true") }) { Text("×") } }
                    }
                }
            }

            Label(attrs = {
                classes(uploader.element("dropzone").className)
                attr("data-over", dragOver.toString())
                onDragEnter { event ->
                    if (event.dataTransfer?.types?.toList()?.contains("Files") == true) {
                        event.preventDefault()
                        dragOver = true
                    }
                }
                onDragOver { event ->
                    if (event.dataTransfer?.types?.toList()?.contains("Files") == true) {
                        event.preventDefault()
                        dragOver = true
                    }
                }
                onDragLeave { dragOver = false }
                onDrop { event ->
                    event.preventDefault()
                    dragOver = false
                    if (!disabled) {
                        event.dataTransfer?.files?.asList()?.let(images::add)
                    }
                }
            }) {
                Text("Bilder hierher ziehen oder auswählen")
                Input(InputType.File) {
                    classes(uploader.element("file-input").className)
                    if (disabled) {
                        attr("disabled", "")
                    }
                    attr("multiple", "")
                    attr("accept", images.extensions.joinToString(",") { ".$it" })
                    attr("aria-describedby", messageId)
                    onChange { event ->
                        event.target.files?.asList()?.let(images::add)
                        // the same file can be chosen again after removing it
                        event.target.value = ""
                    }
                }
            }
        }

        ReorderAnnouncement(reorder)
        ImageDetailsFor(id, images, editing, imageUrl) { editing = null }

        val message = error ?: images.message
        Span({
            classes(field.element(if (message != null) "error" else "hint").className)
            id(messageId)
            attr("role", "status")
        }) {
            Text(
                message ?: if (firstIsMain) {
                    "Das erste Bild ist das Hauptbild. Zum Umsortieren ziehen oder Alt und die Pfeiltasten drücken."
                } else {
                    "Zum Umsortieren ziehen oder Alt und die Pfeiltasten drücken."
                },
            )
        }
    }
}

/** The button over an image tile that opens its details (#105). */
@Composable
internal fun ImageDetailsButton(label: String, onClick: () -> Unit) {
    Button({
        type(ButtonType.Button)
        classes(uploader.element("details").className)
        attr("aria-label", label)
        onClick { onClick() }
    })
}

/** The tags at the bottom of an image tile: "Hauptbild" and, without a description, "Beschreibung fehlt". */
@Composable
internal fun ImageBadges(main: Boolean, missingDescription: Boolean) {
    if (!main && !missingDescription) {
        return
    }
    Span({ classes(uploader.element("badges").className) }) {
        if (main) {
            AdminBadge("Hauptbild", BadgeVariant.TAG)
        }
        if (missingDescription) {
            AdminBadge("Beschreibung fehlt", BadgeVariant.ATTENTION)
        }
    }
}

/** The details dialog of [uuid], one of [images], while it is open; "Übernehmen" changes the record's draft. */
@Composable
internal fun ImageDetailsFor(id: String, images: EditableImages, uuid: String?, imageUrl: (String) -> String, onClose: () -> Unit) {
    AdminImageDetailsDialog(
        open = uuid != null,
        id = "$id-bilddetails",
        imageUrl = uuid?.let(imageUrl),
        details = uuid?.let(images::detailsOf) ?: ImageDetails.NONE,
        onApply = { details ->
            uuid?.let { images.setDetails(it, details) }
            onClose()
        },
        onCancel = onClose,
    )
}
